"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.loadFeaturesXhr = loadFeaturesXhr;
exports.setWithCredentials = setWithCredentials;
exports.xhr = xhr;

var _FormatType = _interopRequireDefault(require("./format/FormatType.js"));

var _functions = require("./functions.js");

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

/**
 * @module ol/featureloader
 */

/**
 *
 * @type {boolean}
 * @private
 */
var withCredentials = false;
/**
 * {@link module:ol/source/Vector~VectorSource} sources use a function of this type to
 * load features.
 *
 * This function takes up to 5 arguments. These are an {@link module:ol/extent~Extent} representing
 * the area to be loaded, a `{number}` representing the resolution (map units per pixel), an
 * {@link module:ol/proj/Projection~Projection} for the projection, an optional success callback that should get
 * the loaded features passed as an argument and an optional failure callback with no arguments. If
 * the callbacks are not used, the corresponding vector source will not fire `'featuresloadend'` and
 * `'featuresloaderror'` events. `this` within the function is bound to the
 * {@link module:ol/source/Vector~VectorSource} it's called from.
 *
 * The function is responsible for loading the features and adding them to the
 * source.
 * @typedef {function(this:(import("./source/Vector").default|import("./VectorTile.js").default),
 *           import("./extent.js").Extent,
 *           number,
 *           import("./proj/Projection.js").default,
 *           function(Array<import("./Feature.js").default>): void=,
 *           function(): void=): void} FeatureLoader
 * @api
 */

/**
 * {@link module:ol/source/Vector~VectorSource} sources use a function of this type to
 * get the url to load features from.
 *
 * This function takes an {@link module:ol/extent~Extent} representing the area
 * to be loaded, a `{number}` representing the resolution (map units per pixel)
 * and an {@link module:ol/proj/Projection~Projection} for the projection  as
 * arguments and returns a `{string}` representing the URL.
 * @typedef {function(import("./extent.js").Extent, number, import("./proj/Projection.js").default): string} FeatureUrlFunction
 * @api
 */

/**
 * @param {string|FeatureUrlFunction} url Feature URL service.
 * @param {import("./format/Feature.js").default} format Feature format.
 * @param {import("./extent.js").Extent} extent Extent.
 * @param {number} resolution Resolution.
 * @param {import("./proj/Projection.js").default} projection Projection.
 * @param {function(Array<import("./Feature.js").default>, import("./proj/Projection.js").default): void} success Success
 *      Function called with the loaded features and optionally with the data projection.
 * @param {function(): void} failure Failure
 *      Function called when loading failed.
 */

function loadFeaturesXhr(url, format, extent, resolution, projection, success, failure) {
  var xhr = new XMLHttpRequest();
  xhr.open('GET', typeof url === 'function' ? url(extent, resolution, projection) : url, true);

  if (format.getType() == _FormatType["default"].ARRAY_BUFFER) {
    xhr.responseType = 'arraybuffer';
  }

  xhr.withCredentials = withCredentials;
  /**
   * @param {Event} event Event.
   * @private
   */

  xhr.onload = function (event) {
    // status will be 0 for file:// urls
    if (!xhr.status || xhr.status >= 200 && xhr.status < 300) {
      var type = format.getType();
      /** @type {Document|Node|Object|string|undefined} */

      var source = void 0;

      if (type == _FormatType["default"].JSON || type == _FormatType["default"].TEXT) {
        source = xhr.responseText;
      } else if (type == _FormatType["default"].XML) {
        source = xhr.responseXML;

        if (!source) {
          source = new DOMParser().parseFromString(xhr.responseText, 'application/xml');
        }
      } else if (type == _FormatType["default"].ARRAY_BUFFER) {
        source =
        /** @type {ArrayBuffer} */
        xhr.response;
      }

      if (source) {
        success(
        /** @type {Array<import("./Feature.js").default>} */
        format.readFeatures(source, {
          extent: extent,
          featureProjection: projection
        }), format.readProjection(source));
      } else {
        failure();
      }
    } else {
      failure();
    }
  };
  /**
   * @private
   */


  xhr.onerror = failure;
  xhr.send();
}
/**
 * Create an XHR feature loader for a `url` and `format`. The feature loader
 * loads features (with XHR), parses the features, and adds them to the
 * vector source.
 * @param {string|FeatureUrlFunction} url Feature URL service.
 * @param {import("./format/Feature.js").default} format Feature format.
 * @return {FeatureLoader} The feature loader.
 * @api
 */


function xhr(url, format) {
  /**
   * @param {import("./extent.js").Extent} extent Extent.
   * @param {number} resolution Resolution.
   * @param {import("./proj/Projection.js").default} projection Projection.
   * @param {function(Array<import("./Feature.js").default>): void} [success] Success
   *      Function called when loading succeeded.
   * @param {function(): void} [failure] Failure
   *      Function called when loading failed.
   * @this {import("./source/Vector").default}
   */
  return function (extent, resolution, projection, success, failure) {
    var source =
    /** @type {import("./source/Vector").default} */
    this;
    loadFeaturesXhr(url, format, extent, resolution, projection,
    /**
     * @param {Array<import("./Feature.js").default>} features The loaded features.
     * @param {import("./proj/Projection.js").default} dataProjection Data
     * projection.
     */
    function (features, dataProjection) {
      source.addFeatures(features);

      if (success !== undefined) {
        success(features);
      }
    },
    /* FIXME handle error */
    failure ? failure : _functions.VOID);
  };
}
/**
 * Setter for the withCredentials configuration for the XHR.
 *
 * @param {boolean} xhrWithCredentials The value of withCredentials to set.
 * Compare https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/
 * @api
 */


function setWithCredentials(xhrWithCredentials) {
  withCredentials = xhrWithCredentials;
}
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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